import IAsset from "@/models/asset/Asset";
import { isErrorWithMessage } from "@/models/helper/ErrorWithMessage";
import { PaginatedResponse } from "@/models/helper/Response";
import { UserFacingError } from "@/models/helper/UserFacingError";
import axios, { isAxiosError } from "axios";

export default class AssetBorrowingService {
  // OLD CODE:
  // static async getBorrowableAssetByRooms(
  //   userEmail: string,
  //   userRole: "Student" | "Assistant",
  //   page: number,
  //   rooms?: number[],
  //   search?: string
  // ): Promise<PaginatedResponse<IAsset[]>> {
  //   try {
  //     const res = await axios.post(
  //       import.meta.env.VITE_API_URL + "asset-borrowing/borrowable-assets",
  //       {
  //         data: {
  //           user_email: userEmail,
  //           user_role: userRole,
  //           page: page,
  //           rooms: rooms,
  //           is_paginate: true,
  //           search: search,
  //         },
  //       }
  //     );
  // 
  //     return res.data.data;
  //   } catch (error) {
  //     console.error(error);
  //     throw new UserFacingError({
  //       title: "Fetch error.",
  //       description: "Failed to fetch list of borrowable assets.",
  //     });
  //   }
  // }

  // NEW CODE: Supports date & shift filtering for available assets
  static async getBorrowableAssetByRooms(
    userEmail: string,
    userRole: "Student" | "Assistant",
    page: number,
    rooms?: number[],
    search?: string,
    checkOutDate?: Date,
    checkInDate?: Date,
    borrowShift?: number,
    returnShift?: number
  ): Promise<PaginatedResponse<IAsset[]>> {
    const formatDateString = (dateString: Date) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    try {
      const res = await axios.post(
        import.meta.env.VITE_API_URL + "asset-borrowing/borrowable-assets",
        {
          data: {
            user_email: userEmail,
            user_role: userRole,
            page: page,
            rooms: rooms,
            is_paginate: true,
            search: search,
            check_out_date: checkOutDate ? formatDateString(checkOutDate) : undefined,
            check_in_date: checkInDate ? formatDateString(checkInDate) : undefined,
            borrow_shift: borrowShift,
            return_shift: returnShift,
          },
        }
      );

      return res.data.data;
    } catch (error) {
      console.error(error);
      throw new UserFacingError({
        title: "Fetch error.",
        description: "Failed to fetch list of borrowable assets.",
      });
    }
  }

  // CHANGED = old using json, must be updated to support new file upload from student
  // static async submitAssetBorrowing(
  //   assets: IAsset[],
  //   checkOutDate: Date,
  //   borrowShift: number,
  //   checkInDate: Date,
  //   returnShift: number,
  //   purpose: string,
  //   borrowerPhoneNumber: string,
  //   studentNimLeader: string,
  //   borrowerEmail: string,
  //   borrowerName: string
  // ) {
  //   // TODO: refactor
  //   const formatDateString = (dateString: Date) => {
  //     const date = new Date(dateString);

  //     const year = date.getFullYear();
  //     const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based
  //     const day = date.getDate().toString().padStart(2, "0");

  //     return `${year}-${month}-${day}`; // Format as month/day/year
  //   };

  //   try {
  //     const res = await axios.post(
  //       import.meta.env.VITE_API_URL + "asset-borrowing/student/submit",
  //       {
  //         check_in_date: formatDateString(checkInDate),
  //         check_out_date: formatDateString(checkOutDate),
  //         borrow_shift: borrowShift,
  //         return_shift: returnShift,
  //         purpose: purpose,
  //         assets: assets.map((asset) => asset.id),
  //         student_nim_leader: studentNimLeader,
  //         borrower_email: borrowerEmail,
  //         borrower_name: borrowerName,
  //         borrower_phone_number: borrowerPhoneNumber,
  //       }
  //     );

  //     return res.data.data;
  //   } catch (error) {
  //     console.error(error);
  //     if (isAxiosError(error)) {
  //       throw new UserFacingError({
  //         title: "Submit borrowing error.",
  //         description:
  //           error.response?.data?.errors ?? "An unknown error occurred.",
  //       });
  //     }
  //     // else
  //     throw new UserFacingError({
  //       title: "Submit borrowing error.",
  //       description: isErrorWithMessage(error)
  //         ? error.message
  //         : "An unknown error occurred.",
  //     });
  //   }
  // }

  static async submitAssetBorrowing(
    submissionRole: "student" | "staff",
    assets: IAsset[],
    checkOutDate: Date,
    borrowShift: number,
    checkInDate: Date,
    returnShift: number,
    purpose: string,
    borrowerPhoneNumber: string,
    studentNimLeader: string,
    borrowerEmail: string,
    borrowerName: string
  ) {
    const formatDateString = (dateString: Date) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    try {
      // 1. Create the FormData object
      const formData = new FormData();

      // 2. Append all fields as strings
      formData.append("check_in_date", formatDateString(checkInDate));
      formData.append("check_out_date", formatDateString(checkOutDate));
      formData.append("borrow_shift", borrowShift.toString());
      formData.append("return_shift", returnShift.toString());
      formData.append("purpose", purpose);
      formData.append("borrower_phone_number", borrowerPhoneNumber);
      formData.append("student_nim_leader", studentNimLeader);
      formData.append("borrower_email", borrowerEmail);
      formData.append("borrower_name", borrowerName);

      // 3. Convert assets array to a JSON string (required for backend parsing)
      const assetIds = assets.map((asset) => asset.id);
      formData.append("assets", JSON.stringify(assetIds));

      // 4. Execute request with multipart headers
      const res = await axios.post(
        import.meta.env.VITE_API_URL + `asset-borrowing/${submissionRole}/submit`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      return res.data.data;
    } catch (error) {
      console.error(error);
      if (isAxiosError(error)) {
        // Before Individual Asset Borrowing React Crash
        // throw new UserFacingError({
        //   title: "Submit borrowing error.",
        //   description:
        //     error.response?.data?.errors ?? "An unknown error occurred.",
        // });

        let errMsg = "An unknown error occurred.";
        const errPayload = error.response?.data?.errors;

        if (typeof errPayload === "string") {
          errMsg = errPayload;
        } else if (Array.isArray(errPayload)) {
          errMsg = errPayload.map((e: any) => e.message || (typeof e === 'string' ? e : JSON.stringify(e))).join(", ");
        } else if (typeof errPayload === "object" && errPayload !== null && errPayload.message) {
          errMsg = String(errPayload.message);
        } else if (errPayload) {
          errMsg = JSON.stringify(errPayload);
        }

        throw new UserFacingError({
          title: "Submit borrowing error.",
          description: errMsg,
        });
      }
      throw new UserFacingError({
        title: "Submit borrowing error.",
        description: isErrorWithMessage(error)
          ? error.message
          : "An unknown error occurred.",
      });
    }
  }
}
